/**
 * Sends GA4 product list events (view_item_list / select_item) through the
 * Google tag the Google & YouTube app already loads on the storefront.
 * No second tag is loaded: a Google tag inside a custom pixel duplicated
 * Google Ads page views and is flagged by Google as unsupported.
 * Consent is handled by Google consent mode, like the app's own events.
 *
 * Markup: data-module="product-list-tracking" data-list-id="..." data-list-name="..."
 * Cards may render asynchronously (product-recommendations), so items are
 * read from the DOM when the list becomes visible and clicks are delegated.
 */

const GA4_MEASUREMENT_ID = 'G-56DSTFY2NS'
// Country segment of the Google & YouTube app item_id. Fixed per store (feed country),
// not the visitor's country, so list events join the app's item reports.
const ITEM_ID_COUNTRY = 'GB'

const CARD_SELECTOR = '.redesign-product-card[data-product-id]'
const LINK_SELECTOR = '.redesign-product-card__link'
const VIEW_THRESHOLD = 0.3

const track = (eventName, list, items) => {
  if (typeof window.gtag !== 'function') return

  window.gtag('event', eventName, {
    // GA4 only: keep these events out of the linked Google Ads destination
    send_to: GA4_MEASUREMENT_ID,
    item_list_id: list.item_list_id,
    item_list_name: list.item_list_name,
    items
  })
}

const getCards = (el) => Array.from(el.querySelectorAll(CARD_SELECTOR))

const toItem = (card, index, list) => {
  const link = card.querySelector(LINK_SELECTOR)
  const { productId, variantId } = card.dataset

  return {
    item_id: variantId ? `shopify_${ITEM_ID_COUNTRY}_${productId}_${variantId}` : productId,
    item_name: link ? link.getAttribute('aria-label') : '',
    index: index + 1,
    item_list_id: list.item_list_id,
    item_list_name: list.item_list_name
  }
}

export default (el) => {
  const list = {
    item_list_id: el.dataset.listId,
    item_list_name: el.dataset.listName
  }

  const trackView = () => {
    const items = getCards(el).map((card, index) => toItem(card, index, list))
    track('view_item_list', list, items)
  }

  const whenCardsExist = (callback) => {
    if (getCards(el).length) {
      callback()
      return
    }

    const mutationObserver = new MutationObserver(() => {
      if (!getCards(el).length) return

      mutationObserver.disconnect()
      callback()
    })

    mutationObserver.observe(el, { childList: true, subtree: true })
  }

  const intersectionObserver = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return
    if (!getCards(el).length) return

    intersectionObserver.disconnect()
    trackView()
  }, { threshold: VIEW_THRESHOLD })

  // Observe only once cards exist: an empty async shell has no height to intersect
  whenCardsExist(() => intersectionObserver.observe(el))

  el.addEventListener('click', (event) => {
    const link = event.target.closest(LINK_SELECTOR)
    if (!link || !el.contains(link)) return

    const cards = getCards(el)
    const card = link.closest(CARD_SELECTOR)
    const index = cards.indexOf(card)
    if (index === -1) return

    track('select_item', list, [toItem(card, index, list)])
  })
}
